import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { sanitizarTextoParaMemed, validarHistoriaClinica } from '@/utils/textSanitizer';

interface SOAPEditorProps {
  initialData?: {
    subjective?: string;
    objective?: string;
    assessment?: string;
    plan?: string;
  };
  onSave?: (data: {
    subjective: string;
    objective: string;
    assessment: string;
    plan: string;
  }) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

export function SOAPEditor({
  initialData = {},
  onSave,
  disabled = false,
  isLoading = false,
}: SOAPEditorProps) {
  const [subjective, setSubjective] = useState(initialData.subjective || '');
  const [objective, setObjective] = useState(initialData.objective || '');
  const [assessment, setAssessment] = useState(initialData.assessment || '');
  const [plan, setPlan] = useState(initialData.plan || '');
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = () => {
    if (!subjective.trim() && !objective.trim() && !assessment.trim() && !plan.trim()) {
      return;
    }
    onSave?.({
      subjective: subjective.trim(),
      objective: objective.trim(),
      assessment: assessment.trim(),
      plan: plan.trim(),
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setSubjective(initialData.subjective || '');
    setObjective(initialData.objective || '');
    setAssessment(initialData.assessment || '');
    setPlan(initialData.plan || '');
    setIsEditing(false);
  };

  const hasData = subjective || objective || assessment || plan;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">SOAP</h3>
        {!isEditing && !disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            {hasData ? 'Editar SOAP' : 'Adicionar SOAP'}
          </Button>
        )}
      </div>

      {isEditing ? (
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-2 text-sm font-medium text-blue-700">
                  <span className="w-6 h-6 flex items-center justify-center bg-blue-100 rounded-full text-xs font-bold">
                    S
                  </span>
                  Subjetivo (História Clínica)
                </label>
                <span
                  className={`text-[11px] font-mono ${
                    subjective.length > 1900
                      ? 'text-amber-600 font-bold'
                      : subjective.length > 2000
                      ? 'text-destructive font-bold'
                      : 'text-gray-500'
                  }`}
                >
                  {subjective.length} / 2.000 caracteres
                </span>
              </div>
              <textarea
                value={subjective}
                onChange={(e) => setSubjective(validarHistoriaClinica(sanitizarTextoParaMemed(e.target.value)))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="O que o paciente relata (sintomas, descrição da dor, histórico)..."
                disabled={disabled || isLoading}
                maxLength={2000}
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-green-700 mb-2">
                <span className="w-6 h-6 flex items-center justify-center bg-green-100 rounded-full text-xs font-bold">
                  O
                </span>
                Objetivo
              </label>
              <textarea
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Achados objetivos (exame físico, sinais vitais, resultados)..."
                disabled={disabled || isLoading}
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-purple-700 mb-2">
                <span className="w-6 h-6 flex items-center justify-center bg-purple-100 rounded-full text-xs font-bold">
                  A
                </span>
                Avaliação
              </label>
              <textarea
                value={assessment}
                onChange={(e) => setAssessment(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Diagnóstico ou avaliação clínica (CID-10, hipóteses)..."
                disabled={disabled || isLoading}
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-orange-700 mb-2">
                <span className="w-6 h-6 flex items-center justify-center bg-orange-100 rounded-full text-xs font-bold">
                  P
                </span>
                Plano
              </label>
              <textarea
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Plano de tratamento (medicamentos, exames, procedimentos)..."
                disabled={disabled || isLoading}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Salvando...' : 'Salvar SOAP'}
            </Button>
          </div>
        </form>
      ) : hasData ? (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
          {subjective && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-blue-700 mb-2">
                <span className="w-6 h-6 flex items-center justify-center bg-blue-100 rounded-full text-xs font-bold">S</span>
                Subjetivo
              </label>
              <p className="text-sm text-gray-900 whitespace-pre-wrap pl-8">{subjective}</p>
            </div>
          )}
          {objective && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-green-700 mb-2">
                <span className="w-6 h-6 flex items-center justify-center bg-green-100 rounded-full text-xs font-bold">O</span>
                Objetivo
              </label>
              <p className="text-sm text-gray-900 whitespace-pre-wrap pl-8">{objective}</p>
            </div>
          )}
          {assessment && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-purple-700 mb-2">
                <span className="w-6 h-6 flex items-center justify-center bg-purple-100 rounded-full text-xs font-bold">A</span>
                Avaliação
              </label>
              <p className="text-sm text-gray-900 whitespace-pre-wrap pl-8">{assessment}</p>
            </div>
          )}
          {plan && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-orange-700 mb-2">
                <span className="w-6 h-6 flex items-center justify-center bg-orange-100 rounded-full text-xs font-bold">P</span>
                Plano
              </label>
              <p className="text-sm text-gray-900 whitespace-pre-wrap pl-8">{plan}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
          <p className="text-sm">Nenhum registro SOAP.</p>
          <p className="text-xs mt-1">Clique em "Adicionar SOAP" para começar.</p>
        </div>
      )}
    </div>
  );
}
